const BASE_URL = import.meta.env.BASE_URL || ''

interface ServerUrls {
  webSocketUrl: URL
  indexUrl: URL
  repositoryUrl: URL
  repositoryEventsUrl: URL
  spellcheckUrl: URL
  userUrl: URL
  faroUrl: URL
  baboonUrl: URL
  imageSearchUrl: URL
  /** Translation service. Optional — not all deployments have it configured. */
  ntbUrl?: URL
}

interface ServerEnvs {
  systemLanguage: string
  imageSearchProvider: string
  environment: string
  /** What the byline reads when an article credits the organisation. Empty if not set. */
  organisationName: string
}

type FeatureFlags = Record<string, boolean>

interface ServerConfig {
  urls: ServerUrls
  envs: ServerEnvs
  featureFlags: FeatureFlags
}

export async function getServerEnvs(): Promise<ServerConfig> {
  const response = await fetch(`${BASE_URL}/api/envs`)

  if (!response.ok) {
    throw new Error(`Failed fetching server envs, got response ${response.status}`)
  }

  try {
    const data = await response.json() as Record<string, unknown>
    const urlAttributes = [
      'webSocketUrl', 'indexUrl', 'repositoryUrl', 'imageSearchUrl',
      'spellcheckUrl', 'userUrl', 'faroUrl', 'baboonUrl'
    ]

    const urls = {} as Record<string, URL>

    for (const field of urlAttributes) {
      const value = data[field]

      if (typeof value !== 'string' || value === '') {
        throw new Error(`missing '${field}' server URL`)
      }

      urls[field] = new URL(value)
    }

    // Optional URLs — empty values mean the corresponding feature is not
    // configured for this deployment, not a misconfiguration.
    const optionalUrlAttributes = ['ntbUrl']
    for (const field of optionalUrlAttributes) {
      const value = data[field]
      if (typeof value === 'string' && value !== '') {
        urls[field] = new URL(value)
      }
    }

    if (!data['systemLanguage'] || typeof data['systemLanguage'] !== 'string') {
      throw new Error('missing \'systemLanguage\' server environment variable')
    }

    if (!data['environment'] || typeof data['environment'] !== 'string') {
      throw new Error('missing \'environment\' server environment variable')
    }

    return {
      urls: {
        ...urls,
        repositoryEventsUrl: new URL('/sse', urls['repositoryUrl'])
      } as ServerUrls,
      envs: {
        systemLanguage: data['systemLanguage'],
        imageSearchProvider: typeof data.imageSearchProvider === 'string' ? data.imageSearchProvider : '',
        environment: typeof data['environment'] === 'string' ? data['environment'] : '',
        organisationName: typeof data['organisationName'] === 'string'
          ? data['organisationName']
          : ''
      },
      featureFlags: {
        // The server normalises these to real booleans, so anything else is not enabled.
        hasPrint: data['hasPrint'] === true,
        hasHast: data['hasHast'] === true,
        hasLooseSlugline: data['hasLooseSlugline'] === true,
        hasVignette: data['hasVignette'] === true,
        hasMemoCreation: data['hasMemoCreation'] === true,
        hasBylineCredit: data['hasBylineCredit'] === true
      }
    }
  } catch (ex) {
    const cause = ex instanceof Error ? ex : undefined
    const detail = cause?.message ? `: ${cause.message}` : ''
    throw new Error(`Failed fetching server envs${detail}`, { cause })
  }
}
